import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ROLE_LABELS } from "@/auth";
import { toast } from "sonner";
import {
  ScanLine, Upload, Loader2, CheckCircle2, AlertTriangle, IdCard,
} from "lucide-react";

export default function RecruitPage() {
  const { code } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [ocrConfidence, setOcrConfidence] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    full_name: "",
    curp: "",
    address: "",
    colonia: "",
    estado: "",
    municipio: "",
    seccion_electoral: "",
    phone: "",
    email: "",
  });

  useEffect(() => {
    api.get(`/public/links/${code}`)
      .then((r) => {
        setMeta(r.data);
        // Pre-fill estado from subaccount
        setForm((f) => ({
          ...f,
          estado: r.data.subaccount.entidad_nombre.toUpperCase(),
          municipio: r.data.subaccount.municipio_nombre ? r.data.subaccount.municipio_nombre.toUpperCase() : "",
        }));
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Enlace no válido");
      });
  }, [code]);

  const onFile = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Formato no soportado. Usa JPG, PNG o WEBP.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("La imagen es muy grande (máx 8 MB).");
      return;
    }
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      setImagePreview(dataUrl);
      await runOcr(dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const runOcr = async (dataUrl) => {
    setOcrLoading(true);
    setOcrConfidence(null);
    try {
      const r = await api.post("/public/ocr/ine", { code, image_base64: dataUrl });
      const d = r.data;
      if (d.error) {
        toast.error(d.error);
        return;
      }
      setForm((f) => ({
        ...f,
        full_name: d.full_name || f.full_name,
        curp: d.curp || f.curp,
        address: d.address || f.address,
        colonia: d.colonia || f.colonia,
        estado: d.estado || f.estado,
        municipio: d.municipio || f.municipio,
        seccion_electoral: d.seccion_electoral || f.seccion_electoral,
      }));
      setOcrConfidence(d.confidence);
      toast.success("Datos extraídos. Revisa y completa tu teléfono y correo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error procesando OCR");
    } finally {
      setOcrLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/volunteers/register", { ...form, code });
      setSubmitted(true);
      toast.success("¡Registro completado!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrar");
    }
  };

  if (error) {
    return (
      <ErrorState message={error} />
    );
  }
  if (!meta) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Cargando…</div>;
  }
  if (submitted) {
    return (
      <SuccessState meta={meta} />
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="recruit-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-3">
          <div className="w-8 h-8 bg-slate-900 grid place-items-center rounded">
            <span className="text-white font-display font-black text-lg">W</span>
          </div>
          <div>
            <div className="font-display font-bold text-slate-900 text-base leading-none">Noctua 360</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Registro de voluntario</div>
          </div>
        </div>
        <div className="brand-stripe" />
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="surface-card p-6">
          <div className="flex items-start gap-4">
            <IdCard className="w-8 h-8 text-blue-600 shrink-0" />
            <div>
              <h1 className="font-display font-black text-2xl sm:text-3xl text-slate-900">
                Súmate como voluntario
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Campaña <strong>{meta.subaccount.candidate_name}</strong> · {meta.subaccount.entidad_nombre}
                {meta.subaccount.municipio_nombre ? ` · ${meta.subaccount.municipio_nombre}` : ""}
              </p>
              <div className="mt-2 text-xs text-slate-500">
                Te asignarás como <strong>{ROLE_LABELS[meta.link.target_role]}</strong> en{" "}
                <strong>{meta.link.target_region_name}</strong>
                {meta.link.municipio_nombre && ` (${meta.link.municipio_nombre})`}.
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-6 space-y-6" data-testid="recruit-form">
          {/* OCR Upload zone */}
          <section className="surface-card p-6">
            <h2 className="font-display font-bold text-lg text-slate-900">Carga o escanea tu INE</h2>
            <p className="text-sm text-slate-600 mt-1">
              Subimos la imagen solo para extraer tus datos. <strong>No la guardamos.</strong>
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp"
              capture="environment"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="ine-file-input"
            />
            <div
              className="mt-4 border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => fileRef.current?.click()}
              data-testid="ine-upload-zone"
            >
              {imagePreview ? (
                <div className="space-y-3">
                  <img src={imagePreview} alt="INE" className="max-h-48 mx-auto rounded border border-slate-200" />
                  <div className="text-xs text-slate-500">Toca para cambiar la imagen</div>
                </div>
              ) : (
                <div>
                  <ScanLine className="w-10 h-10 mx-auto text-slate-400" />
                  <div className="mt-3 font-display font-bold text-slate-900">Subir imagen del frente de tu INE</div>
                  <div className="text-xs text-slate-500 mt-1">JPG, PNG o WEBP · máx 8 MB</div>
                  <Button type="button" className="mt-4 btn-accent" data-testid="ine-upload-button">
                    <Upload className="w-4 h-4 mr-2" /> Seleccionar imagen
                  </Button>
                </div>
              )}
              {ocrLoading && (
                <div className="mt-4 flex items-center justify-center gap-2 text-blue-700" data-testid="ocr-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Extrayendo datos…
                </div>
              )}
              {ocrConfidence !== null && !ocrLoading && (
                <div className="mt-3 inline-flex items-center gap-1 text-xs px-2 py-1 bg-green-100 text-green-800 rounded" data-testid="ocr-confidence">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Datos extraídos · confianza {(ocrConfidence * 100).toFixed(0)}%
                </div>
              )}
            </div>
          </section>

          {/* Form fields */}
          <section className="surface-card p-6 space-y-4">
            <h2 className="font-display font-bold text-lg text-slate-900">Tus datos</h2>
            <p className="text-xs text-slate-500 -mt-2">Datos extraídos del INE. Revisa y corrige si es necesario.</p>
            <Field label="Nombre completo">
              <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="form-full-name" />
            </Field>
            <Field label="CURP">
              <Input required maxLength={18} value={form.curp} onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })} data-testid="form-curp" />
            </Field>
            <Field label="Dirección (calle y número)">
              <Input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="form-address" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Colonia">
                <Input required value={form.colonia} onChange={(e) => setForm({ ...form, colonia: e.target.value })} data-testid="form-colonia" />
              </Field>
              <Field label="Sección electoral">
                <Input required value={form.seccion_electoral} onChange={(e) => setForm({ ...form, seccion_electoral: e.target.value })} data-testid="form-seccion" />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Municipio">
                <Input required value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} data-testid="form-municipio" />
              </Field>
              <Field label="Estado">
                <Input required value={form.estado} onChange={(e) => setForm({ ...form, estado: e.target.value })} data-testid="form-estado" />
              </Field>
            </div>

            <div className="pt-3 border-t border-slate-200">
              <h3 className="font-display font-bold text-slate-900">Contacto (lo completas tú)</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Teléfono">
                <Input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" />
              </Field>
              <Field label="Correo electrónico">
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" />
              </Field>
            </div>
          </section>

          <Button type="submit" className="btn-primary w-full h-12" data-testid="recruit-submit">
            Completar registro
          </Button>
        </form>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SuccessState({ meta }) {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
      <div className="surface-card p-10 max-w-md w-full text-center">
        <CheckCircle2 className="w-14 h-14 mx-auto text-green-600" />
        <h1 className="mt-4 font-display font-black text-2xl text-slate-900">¡Listo!</h1>
        <p className="mt-2 text-sm text-slate-600">
          Tu registro como voluntario para la campaña de <strong>{meta.subaccount.candidate_name}</strong> fue recibido correctamente.
        </p>
        <p className="mt-4 text-xs text-slate-500">Pronto te contactaremos con los próximos pasos.</p>
      </div>
    </div>
  );
}

function ErrorState({ message }) {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-6">
      <div className="surface-card p-10 max-w-md w-full text-center" data-testid="recruit-error">
        <AlertTriangle className="w-14 h-14 mx-auto text-red-600" />
        <h1 className="mt-4 font-display font-black text-2xl text-slate-900">Enlace no disponible</h1>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        <p className="mt-4 text-xs text-slate-500">
          Contacta al equipo de la campaña para obtener un enlace válido.
        </p>
      </div>
    </div>
  );
}
